import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { RadarChart } from '../shared/components/dashboard/RadarChart';
import { InvestorCard } from '../shared/components/dashboard/InvestorCard';
import { TraitBars } from '../shared/components/dashboard/TraitBars';
import { traitKeys, type TraitKey } from '../shared/constants/traits';
import type { DashboardInvestor } from '../shared/providers/dashboardTypes';
import type { PersonaData } from '../shared/services/personaService';
import { useDashboardData } from '../shared/providers/dashboardContext';

const teamAssignments = ['7A', '6B', '7C', '5F'] as const;
type TraitSet = DashboardInvestor['traits'];

const traitFieldMap: Record<TraitKey, keyof PersonaData> = {
  macroVsMicro: 'macroVsMicro',
  valueVsGrowth: 'valueVsGrowth',
  timeHorizon: 'timeHorizon',
  passiveVsActive: 'passiveVsActive',
  strategicVsFinancial: 'strategicVsFinancial',
};

function clampTrait(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }
  if (value >= 0 && value <= 1) {
    return Math.round(value * 100);
  }
  return Math.max(0, Math.min(100, Math.round(value)));
}

function getPersonaTraitValue(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return clampTrait(value);
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return clampTrait(parsed);
    }
  }
  return null;
}

function toTraitSet(
  persona: PersonaData | null | undefined
): Record<TraitKey, number | null> {
  const empty: Record<TraitKey, number | null> = {
    macroVsMicro: null,
    valueVsGrowth: null,
    timeHorizon: null,
    passiveVsActive: null,
    strategicVsFinancial: null,
  };
  if (!persona) return empty;

  const result = { ...empty } as Record<TraitKey, number | null>;
  for (const key of traitKeys) {
    const value = getPersonaTraitValue(persona[traitFieldMap[key]]);
    result[key] = value;
  }
  return result;
}

type DashboardInvestorsPageProps = {
  suppressEmptyPersonaMessage?: boolean;
  showFavouriteSection?: boolean;
  allowRegeneration?: boolean;
};

export function DashboardInvestorsPage({
  suppressEmptyPersonaMessage = false,
  showFavouriteSection = true,
  allowRegeneration = true,
}: DashboardInvestorsPageProps = {}) {
  const [showPersonaNotes, setShowPersonaNotes] = useState(false);
  const {
    personasLoading,
    personaError,
    investors,
    investorsLoading,
    investorsError,
    selectedInvestor,
    setSelectedInvestor,
    favourites,
    favouriteError,
    regenerateFavourite,
    regeneratingFavouriteKeys,
    autoFetchAttemptedKeys,
    markAutoFetchAttempted,
  } = useDashboardData();

  const investorTableMap = useMemo(() => {
    const map = new Map<string, string>();
    investors.forEach((investor, index) => {
      const key = investor.id ?? investor.name;
      map.set(key, teamAssignments[index % teamAssignments.length]);
    });
    return map;
  }, [investors]);

  const investorKey = useMemo(
    () => (selectedInvestor ? selectedInvestor.id ?? selectedInvestor.name : null),
    [selectedInvestor]
  );

  const favouriteEntry = useMemo(
    () => (investorKey ? favourites.get(investorKey) ?? null : null),
    [favourites, investorKey]
  );

  const favouritePersona = favouriteEntry?.persona ?? null;
  const favouritePersonaTraits = toTraitSet(favouritePersona);
  const canRenderFavourite = Boolean(favouritePersona);
  const isRegeneratingFavourite = Boolean(investorKey && regeneratingFavouriteKeys.has(investorKey));
  const showFavouriteLoadingState = isRegeneratingFavourite && !canRenderFavourite;
  const fallbackMessage = favouriteError ?? 'Click to calculate the winner.';
  const showFallbackMessage = !(suppressEmptyPersonaMessage && !favouriteError);
  const questionsAsked = Array.isArray(favouritePersona?.questionsAsked)
    ? favouritePersona.questionsAsked
    : [];
  const keyInsightSegments = segmentPersonaText(favouritePersona?.keyInsights ?? []);

  const loading = investorsLoading || personasLoading;
  const assignedTable = useMemo(() => {
    if (!selectedInvestor) {
      return teamAssignments[0];
    }
    const key = investorKey ?? '';
    return investorTableMap.get(key) ?? teamAssignments[0];
  }, [investorKey, investorTableMap, selectedInvestor]);

  useEffect(() => {
    if (!selectedInvestor || !investorKey) return;
    const hasFavourite = favourites.has(investorKey);
    const isLoading = regeneratingFavouriteKeys.has(investorKey);
    const attempted = autoFetchAttemptedKeys.has(investorKey);
    if (!hasFavourite && !isLoading && !attempted) {
      markAutoFetchAttempted(investorKey);
      void regenerateFavourite(selectedInvestor);
    }
  }, [
    selectedInvestor,
    investorKey,
    favourites,
    regeneratingFavouriteKeys,
    autoFetchAttemptedKeys,
    markAutoFetchAttempted,
    regenerateFavourite,
  ]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#291B4C] animate-spin mx-auto mb-4" strokeWidth={2} />
          <p className="text-lg font-semibold text-[#291B4C]">Loading Investors...</p>
        </div>
      </div>
    );
  }

  if (!selectedInvestor || investors.length === 0) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6 text-center text-[#291B4C]/70">
        <div>
          <h2 className="text-2xl font-bold uppercase tracking-tight">Investors unavailable</h2>
          <p className="mt-3 text-sm leading-relaxed">
            {investorsError ??
              'No investor profiles are currently available. Seed investor data via the backend to enable this dashboard.'}
          </p>
        </div>
      </div>
    );
  }

  // Using shared TraitBars component

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-[1400px] mx-auto px-12 py-12 space-y-12">
        {investorsError && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
            {investorsError}
          </div>
        )}
        {personaError && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
            {personaError}
          </div>
        )}

        <div className="grid grid-cols-4 gap-6">
          {investors.map((investor) => {
            const isSelected = investor.name === selectedInvestor.name;
            return (
              <InvestorCard
                key={investor.id ?? investor.name}
                name={investor.name}
                photo={investor.photo ?? undefined}
                isSelected={Boolean(isSelected)}
                onClick={() => setSelectedInvestor(investor)}
              />
            );
          })}
        </div>

        <div className="border border-[#291B4C]/10 rounded-2xl p-10 bg-white">
          <div className="grid grid-cols-5 gap-16">
            <div className="col-span-2 space-y-8">
              <div>
                <h2 className="text-4xl font-bold text-[#291B4C] mb-3 uppercase tracking-tight">
                  {selectedInvestor.name}
                </h2>
                <p className="text-base text-[#291B4C]/60 leading-relaxed">
                  {selectedInvestor.description}
                </p>
              </div>

              <div className="space-y-6">
                <h3 className="text-lg font-bold text-[#291B4C] uppercase tracking-wider">
                  Investment Traits
                </h3>
                <TraitBars traits={selectedInvestor.traits as TraitSet} />
              </div>
            </div>

            <div className="col-span-3 flex items-center justify-center">
              <RadarChart data={selectedInvestor.traits} color="#291B4C" size={480} />
            </div>
          </div>
        </div>

        {(() => {
          if (!showFavouriteSection) return null;
          const shouldRenderFavouriteSection = canRenderFavourite || isRegeneratingFavourite || allowRegeneration;
          if (!shouldRenderFavouriteSection) return null;
          return (
            <div className="border border-[#291B4C]/10 rounded-2xl p-10 bg-white">
          {showFavouriteLoadingState ? (
            <div className="flex flex-col items-center justify-center gap-3 text-[#291B4C]/60">
              <Loader2 className="w-8 h-8 animate-spin text-[#291B4C]" strokeWidth={2} />
              <span className="text-sm font-medium uppercase tracking-wider">Matching personas...</span>
            </div>
          ) : canRenderFavourite ? (
            <>
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-8">
                <h3 className="text-2xl font-bold text-[#291B4C] uppercase tracking-tight">
                  {selectedInvestor.name}'s favourite persona is from{' '}
                  <span className="underline">
                    {favouritePersona.teamName ?? assignedTable}
                  </span>
                </h3>
                {allowRegeneration && (
                  <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowPersonaNotes(false);
                        regenerateFavourite(selectedInvestor);
                      }}
                      className="inline-flex items-center justify-center rounded-full border border-[#291B4C]/20 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-[#291B4C] transition-colors hover:bg-[#291B4C] hover:text-white disabled:opacity-60 disabled:cursor-not-allowed"
                      disabled={isRegeneratingFavourite}
                    >
                      {isRegeneratingFavourite ? (
                        <span className="inline-flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
                          Regenerating...
                        </span>
                      ) : (
                        'Regenerate favourite'
                      )}
                    </button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-5 gap-16">
                <div className="col-span-5 grid gap-8">
                  <div className="space-y-6">
                    <h4 className="text-lg font-bold text-[#291B4C] uppercase tracking-wider">
                      Traits of {selectedInvestor.name}'s favourite persona
                    </h4>
                    <TraitBars traits={favouritePersonaTraits} />
                  </div>
                </div>
              </div>

              <div className="mt-8 rounded-2xl border border-[#291B4C]/10 bg-white shadow-sm">
                <div className="flex items-center justify-between p-6">
                  <div>
                    <h4 className="text-sm font-semibold text-[#291B4C]/60 uppercase tracking-wider">
                      Persona Notes
                    </h4>
                    <p className="text-xs text-[#291B4C]/60">Investment philosophy and key insights</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowPersonaNotes((value) => !value)}
                    className="inline-flex items-center gap-2 text-[#291B4C]/70 hover:text-[#291B4C] transition-colors"
                    aria-expanded={showPersonaNotes}
                    aria-controls="persona-notes-content"
                  >
                    <span className="text-sm font-medium">{showPersonaNotes ? 'Hide' : 'Show'}</span>
                    <svg
                      className={`h-5 w-5 transition-transform ${showPersonaNotes ? 'rotate-180' : ''}`}
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
                {showPersonaNotes && (
                  <div id="persona-notes-content" className="border-t border-[#291B4C]/10 p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                      <div className="rounded-2xl border border-[#291B4C]/15 bg-white p-6 shadow-sm flex flex-col lg:col-span-12">
                        <h4 className="text-sm font-semibold text-[#291B4C]/60 uppercase tracking-wider mb-4">
                          Investment Philosophy
                        </h4>
                        <p className="text-sm leading-relaxed text-[#291B4C]/80 whitespace-pre-line">
                          {favouritePersona.investmentPhilosophy ??
                            'No investment philosophy available'}
                        </p>
                      </div>
                      <div className="rounded-2xl border border-[#291B4C]/15 bg-white p-6 shadow-sm flex flex-col lg:col-span-6">
                        <h4 className="text-sm font-semibold text-[#291B4C]/60 uppercase tracking-wider mb-4">
                          Key Insights
                        </h4>
                        {keyInsightSegments.length > 0 ? (
                          <div className="space-y-3 text-sm leading-relaxed text-[#291B4C]/80">
                            {keyInsightSegments.map((insight, idx) => (
                              <p key={idx}>{insight}</p>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-[#291B4C]/60 italic">No key insights available</p>
                        )}
                      </div>
                      <div className="rounded-2xl border border-[#291B4C]/15 bg-white p-6 shadow-sm flex flex-col lg:col-span-6">
                        <h5 className="font-semibold text-[#291B4C]/60 mb-4 uppercase tracking-wide text-sm">
                          Additional Key Questions
                        </h5>
                        {questionsAsked.length > 0 ? (
                          <ul className="list-disc pl-5 space-y-3 text-sm leading-relaxed text-[#291B4C]/80 marker:text-[#291B4C]">
                            {questionsAsked.map((question, index) => (
                              <li key={index}>{question}</li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-sm text-[#291B4C]/60 italic">No questions available</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : allowRegeneration ? (
            <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
              <div className="space-y-2 text-[#291B4C]/60 text-sm leading-relaxed">
                <p>
                  Find the closest uploaded persona match for{' '}
                  <span className="font-semibold text-[#291B4C]">{selectedInvestor.name}</span> across all
                  workshop submissions.
                </p>
                {showFallbackMessage && (
                  <p className="text-[#291B4C] text-xs font-semibold uppercase tracking-wider">
                    {fallbackMessage}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowPersonaNotes(false);
                  regenerateFavourite(selectedInvestor);
                }}
                className="inline-flex items-center justify-center rounded-full bg-[#291B4C] px-6 py-2 text-sm font-semibold uppercase tracking-wider text-white shadow-sm transition-colors hover:bg-[#291B4C]/90 disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={isRegeneratingFavourite}
              >
                {showFavouriteLoadingState ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
                    Matching personas...
                  </span>
                ) : isRegeneratingFavourite ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
                    Regenerating...
                  </span>
                ) : (
                  'Get favourite persona'
                )}
              </button>
            </div>
          ) : null}
        </div>
          );
        })()}
      </div>
    </div>
  );
}

function segmentPersonaText(value: unknown): string[] {
  if (value === null || value === undefined) return [];

  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string');
  }

  if (typeof value === 'string') {
    return splitSentences(value);
  }

  if (typeof value === 'object') {
    const text = Object.values(value as Record<string, unknown>)
      .filter((item): item is string => typeof item === 'string')
      .join('\n');
    return splitSentences(text);
  }

  return splitSentences(String(value));
}

function splitSentences(input: string): string[] {
  if (!input.trim()) return [];

  return input
    .split(/\r?\n+/)
    .flatMap((chunk) => chunk.match(/[^.!?]+[.!?]?/g) ?? [chunk])
    .map((entry) => entry.trim().replace(/\s+/g, ' '))
    .filter(Boolean);
}
